import { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import axios from "axios";
import { 
  Menu, X, Mail, Phone, MapPin, User, ChevronRight, 
  Monitor, Tv, Settings, Wrench, MessageCircle,
  ArrowRight, Plus, Edit, Trash2, LogOut, Save, Image,
  FileText, HelpCircle, LayoutDashboard, Package, Inbox, Upload,
  Play, Calendar, Sparkles
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Axios instance with auth header
const authAxios = axios.create();
authAxios.interceptors.request.use((config) => {
  const token = localStorage.getItem("access_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  config.withCredentials = true;
  return config;
});

// Auth Context
const AuthContext = createContext(null);

const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const res = await authAxios.get(`${API}/auth/me`);
      setUser(res.data);
    } catch (e) {
      setUser(null);
      localStorage.removeItem("access_token");
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
    if (res.data.token) {
      localStorage.setItem("access_token", res.data.token);
    }
    setUser(res.data);
    return res.data;
  };

  const logout = async () => {
    try {
      await authAxios.post(`${API}/auth/logout`);
    } catch (e) { /* ignore */ }
    localStorage.removeItem("access_token");
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-primary">Loading...</div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/admin/login" replace />;
  }
  
  return children;
};

// ============ ADMIN COMPONENTS ============

const AdminLogin = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) navigate("/admin");
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/admin");
    } catch (e) {
      const detail = e.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="font-unbounded text-3xl font-bold text-primary mb-2">UPCLOSE DIGITAL</h1>
          <p className="text-muted-foreground">Admin Panel</p>
        </div>
        <form onSubmit={handleSubmit} className="bg-card p-8 rounded-lg border border-white/10">
          {error && (
            <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded text-red-400 text-sm" data-testid="login-error">
              {error}
            </div>
          )}
          <div className="mb-4">
            <label className="block text-sm text-muted-foreground mb-2">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 bg-black border border-white/20 rounded-lg text-foreground focus:border-primary focus:ring-1 focus:ring-primary outline-none"
              required
              data-testid="login-email-input"
            />
          </div>
          <div className="mb-6">
            <label className="block text-sm text-muted-foreground mb-2">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 bg-black border border-white/20 rounded-lg text-foreground focus:border-primary focus:ring-1 focus:ring-primary outline-none"
              required
              data-testid="login-password-input"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 bg-primary text-black font-semibold rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50"
            data-testid="login-submit-btn"
          >
            {loading ? "Signing in..." : "Sign In"}
          </button>
        </form>
        <p className="text-center mt-6 text-muted-foreground text-sm">
          <a href="/" className="text-primary hover:underline">← Back to website</a>
        </p>
      </div>
    </div>
  );
};

const AdminLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  const menuItems = [
    { icon: <LayoutDashboard size={20} />, label: "Dashboard", path: "/admin" },
    { icon: <Package size={20} />, label: "Products", path: "/admin/products" },
    { icon: <FileText size={20} />, label: "Blog", path: "/admin/blogs" },
    { icon: <Image size={20} />, label: "Gallery", path: "/admin/gallery" },
    { icon: <HelpCircle size={20} />, label: "FAQ", path: "/admin/faqs" },
    { icon: <Inbox size={20} />, label: "Messages", path: "/admin/messages" },
  ];

  return (
    <div className="min-h-screen bg-background flex">
      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-card border-r border-white/10 transform ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 transition-transform`}>
        <div className="p-6 border-b border-white/10">
          <h2 className="font-unbounded text-lg font-bold text-primary">ADMIN PANEL</h2>
        </div>
        <nav className="p-4">
          {menuItems.map((item) => (
            <a
              key={item.path}
              href={item.path}
              className="flex items-center gap-3 px-4 py-3 text-muted-foreground hover:text-foreground hover:bg-white/5 rounded-lg transition-colors mb-1"
              data-testid={`admin-nav-${item.label.toLowerCase()}`}
            >
              {item.icon}
              {item.label}
            </a>
          ))}
        </nav>
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/10">
          <div className="flex items-center gap-3 mb-4 px-4">
            <div className="w-8 h-8 bg-primary/20 rounded-full flex items-center justify-center">
              <User size={16} className="text-primary" />
            </div>
            <div>
              <p className="text-sm text-foreground">{user?.name || "Admin"}</p>
              <p className="text-xs text-muted-foreground">{user?.email}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 px-4 py-2 text-red-400 hover:bg-red-500/10 rounded-lg w-full transition-colors"
            data-testid="admin-logout-btn"
          >
            <LogOut size={18} />
            Logout
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 min-h-screen">
        {/* Mobile Header */}
        <header className="lg:hidden flex items-center justify-between p-4 border-b border-white/10">
          <h2 className="font-unbounded text-lg font-bold text-primary">ADMIN</h2>
          <button onClick={() => setSidebarOpen(!sidebarOpen)} className="text-foreground">
            {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </header>
        <div className="p-6 lg:p-8">
          {children}
        </div>
      </main>

      {/* Overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 lg:hidden z-40" onClick={() => setSidebarOpen(false)} />
      )}
    </div>
  );
};

const AdminDashboard = () => {
  const [stats, setStats] = useState({ products: 0, blogs: 0, gallery: 0, faqs: 0, messages: 0 });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [products, blogs, gallery, faqs, messages] = await Promise.all([
          authAxios.get(`${API}/admin/products`),
          authAxios.get(`${API}/admin/blogs`),
          authAxios.get(`${API}/admin/gallery`),
          authAxios.get(`${API}/admin/faqs`),
          authAxios.get(`${API}/admin/messages`),
        ]);
        setStats({
          products: products.data.length,
          blogs: blogs.data.length,
          gallery: gallery.data.length,
          faqs: faqs.data.length,
          messages: messages.data.length,
        });
      } catch (e) {
        console.error(e);
      }
    };
    fetchStats();
  }, []);

  const cards = [
    { label: "Products", value: stats.products, icon: <Package size={24} />, href: "/admin/products" },
    { label: "Blog Posts", value: stats.blogs, icon: <FileText size={24} />, href: "/admin/blogs" },
    { label: "Gallery Items", value: stats.gallery, icon: <Image size={24} />, href: "/admin/gallery" },
    { label: "FAQs", value: stats.faqs, icon: <HelpCircle size={24} />, href: "/admin/faqs" },
    { label: "Messages", value: stats.messages, icon: <Inbox size={24} />, href: "/admin/messages" },
  ];

  return (
    <div data-testid="admin-dashboard">
      <h1 className="font-unbounded text-2xl font-bold mb-8">Dashboard</h1>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {cards.map((card) => (
          <a
            key={card.label}
            href={card.href}
            className="bg-card p-6 rounded-lg border border-white/10 hover:border-primary transition-colors group"
            data-testid={`dashboard-card-${card.label.toLowerCase().replace(' ', '-')}`}
          >
            <div className="flex items-center justify-between mb-4">
              <span className="text-primary">{card.icon}</span>
              <ChevronRight size={20} className="text-muted-foreground group-hover:text-primary transition-colors" />
            </div>
            <p className="font-unbounded text-3xl font-bold mb-1">{card.value}</p>
            <p className="text-muted-foreground">{card.label}</p>
          </a>
        ))}
      </div>
      <div className="mt-8 p-6 bg-card rounded-lg border border-white/10">
        <h2 className="font-unbounded text-lg font-semibold mb-4">Quick Actions</h2>
        <div className="flex flex-wrap gap-3">
          <a href="/admin/products" className="px-4 py-2 bg-primary/10 text-primary rounded-lg hover:bg-primary/20 transition-colors flex items-center gap-2">
            <Plus size={18} /> Add Product
          </a>
          <a href="/admin/blogs" className="px-4 py-2 bg-primary/10 text-primary rounded-lg hover:bg-primary/20 transition-colors flex items-center gap-2">
            <Plus size={18} /> Add Blog Post
          </a>
          <a href="/admin/gallery" className="px-4 py-2 bg-primary/10 text-primary rounded-lg hover:bg-primary/20 transition-colors flex items-center gap-2">
            <Plus size={18} /> Add Gallery Item
          </a>
          <a href="/" target="_blank" className="px-4 py-2 bg-white/5 text-foreground rounded-lg hover:bg-white/10 transition-colors">
            View Website →
          </a>
        </div>
      </div>
    </div>
  );
};

// Generic CRUD Component
const AdminCRUD = ({ title, endpoint, fields, renderItem }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({});
  const [showForm, setShowForm] = useState(false);

  const fetchItems = async () => {
    try {
      const res = await authAxios.get(`${API}/admin/${endpoint}`);
      setItems(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  const resetForm = () => {
    setFormData({});
    setEditingId(null);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await authAxios.put(`${API}/admin/${endpoint}/${editingId}`, formData);
      } else {
        await authAxios.post(`${API}/admin/${endpoint}`, formData);
      }
      fetchItems();
      resetForm();
    } catch (e) {
      console.error(e);
      alert("Error saving item");
    }
  };

  const handleEdit = (item) => {
    setFormData(item);
    setEditingId(item.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this item?")) return;
    try {
      await authAxios.delete(`${API}/admin/${endpoint}/${id}`);
      fetchItems();
    } catch (e) {
      console.error(e);
      alert("Error deleting item");
    }
  };

  return (
    <div data-testid={`admin-${endpoint}`}>
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-unbounded text-2xl font-bold">{title}</h1>
        <button
          onClick={() => { resetForm(); setShowForm(true); }}
          className="px-4 py-2 bg-primary text-black font-semibold rounded-lg hover:bg-primary/90 transition-colors flex items-center gap-2"
          data-testid={`add-${endpoint}-btn`}
        >
          <Plus size={18} /> Add New
        </button>
      </div>

      {showForm && (
        <div className="mb-8 bg-card p-6 rounded-lg border border-white/10">
          <h2 className="font-unbounded text-lg font-semibold mb-4">{editingId ? "Edit" : "Add"} {title.slice(0, -1)}</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            {fields.map((field) => (
              <div key={field.name}>
                <label className="block text-sm text-muted-foreground mb-2">{field.label}</label>
                {field.type === "textarea" ? (
                  <textarea
                    value={formData[field.name] || ""}
                    onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
                    className="w-full px-4 py-3 bg-black border border-white/20 rounded-lg text-foreground focus:border-primary focus:ring-1 focus:ring-primary outline-none resize-none"
                    rows={4}
                    required={field.required}
                    data-testid={`form-${field.name}`}
                  />
                ) : (
                  <input
                    type={field.type || "text"}
                    value={formData[field.name] || ""}
                    onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
                    className="w-full px-4 py-3 bg-black border border-white/20 rounded-lg text-foreground focus:border-primary focus:ring-1 focus:ring-primary outline-none"
                    required={field.required}
                    data-testid={`form-${field.name}`}
                  />
                )}
              </div>
            ))}
            <div className="flex gap-3">
              <button
                type="submit"
                className="px-6 py-2 bg-primary text-black font-semibold rounded-lg hover:bg-primary/90 transition-colors flex items-center gap-2"
                data-testid="form-submit-btn"
              >
                <Save size={18} /> {editingId ? "Update" : "Save"}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="px-6 py-2 bg-white/10 text-foreground rounded-lg hover:bg-white/20 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <p className="text-muted-foreground">Loading...</p>
      ) : items.length === 0 ? (
        <p className="text-muted-foreground">No items found. Add your first one!</p>
      ) : (
        <div className="space-y-4">
          {items.map((item) => (
            <div key={item.id} className="bg-card p-4 rounded-lg border border-white/10 flex items-center justify-between gap-4">
              <div className="flex-1 min-w-0">
                {renderItem(item)}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => handleEdit(item)}
                  className="p-2 text-primary hover:bg-primary/10 rounded-lg transition-colors"
                  data-testid={`edit-${item.id}`}
                >
                  <Edit size={18} />
                </button>
                <button
                  onClick={() => handleDelete(item.id)}
                  className="p-2 text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                  data-testid={`delete-${item.id}`}
                >
                  <Trash2 size={18} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Image Upload Component
const ImageUpload = ({ value, onChange, label }) => {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(value || "");

  useEffect(() => {
    setPreview(value || "");
  }, [value]);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert("Image too large. Maximum size is 5MB.");
      return;
    }

    // Check file type
    if (!file.type.startsWith("image/")) {
      alert("Please select an image file.");
      return;
    }

    setUploading(true);

    try {
      // Convert to base64
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = reader.result;
        
        // Upload to server
        const res = await authAxios.post(
          `${API}/admin/upload`,
          { image: base64, filename: file.name }
        );

        // Use the returned URL
        const imageUrl = `${BACKEND_URL}${res.data.url}`;
        setPreview(imageUrl);
        onChange(imageUrl);
        setUploading(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error("Upload failed:", error);
      alert("Failed to upload image. Please try again.");
      setUploading(false);
    }
  };

  return (
    <div>
      <label className="block text-sm text-muted-foreground mb-2">{label}</label>
      <div className="space-y-3">
        {preview && (
          <div className="relative w-32 h-32 rounded-lg overflow-hidden border border-white/20">
            <img src={preview} alt="Preview" className="w-full h-full object-cover" />
          </div>
        )}
        <div className="flex gap-3 items-center">
          <label className="px-4 py-2 bg-primary/10 text-primary rounded-lg hover:bg-primary/20 transition-colors cursor-pointer flex items-center gap-2">
            <Upload size={18} />
            {uploading ? "Uploading..." : "Upload Image"}
            <input
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              className="hidden"
              disabled={uploading}
            />
          </label>
          <span className="text-xs text-muted-foreground">or</span>
          <input
            type="text"
            placeholder="Paste image URL"
            value={value || ""}
            onChange={(e) => {
              onChange(e.target.value);
              setPreview(e.target.value);
            }}
            className="flex-1 px-4 py-2 bg-black border border-white/20 rounded-lg text-foreground text-sm focus:border-primary focus:ring-1 focus:ring-primary outline-none"
          />
        </div>
      </div>
    </div>
  );
};

const AdminProducts = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({});
  const [showForm, setShowForm] = useState(false);

  const fetchItems = async () => {
    try {
      const res = await authAxios.get(`${API}/admin/products`);
      setItems(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchItems(); }, []);

  const resetForm = () => {
    setFormData({});
    setEditingId(null);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await authAxios.put(`${API}/admin/products/${editingId}`, formData);
      } else {
        await authAxios.post(`${API}/admin/products`, formData);
      }
      fetchItems();
      resetForm();
    } catch (e) {
      console.error(e);
      alert("Error saving product");
    }
  };

  const handleEdit = (item) => {
    setFormData(item);
    setEditingId(item.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this product?")) return;
    try {
      await authAxios.delete(`${API}/admin/products/${id}`);
      fetchItems();
    } catch (e) {
      console.error(e);
      alert("Error deleting product");
    }
  };

  return (
    <div data-testid="admin-products">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-unbounded text-2xl font-bold">Products</h1>
        <button
          onClick={() => { resetForm(); setShowForm(true); }}
          className="px-4 py-2 bg-primary text-black font-semibold rounded-lg hover:bg-primary/90 transition-colors flex items-center gap-2"
          data-testid="add-products-btn"
        >
          <Plus size={18} /> Add New
        </button>
      </div>

      {showForm && (
        <div className="mb-8 bg-card p-6 rounded-lg border border-white/10">
          <h2 className="font-unbounded text-lg font-semibold mb-4">{editingId ? "Edit" : "Add"} Product</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm text-muted-foreground mb-2">Product Name</label>
              <input
                type="text"
                value={formData.name || ""}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-4 py-3 bg-black border border-white/20 rounded-lg text-foreground focus:border-primary focus:ring-1 focus:ring-primary outline-none"
                required
                data-testid="form-name"
              />
            </div>
            <div>
              <label className="block text-sm text-muted-foreground mb-2">Specifications</label>
              <input
                type="text"
                value={formData.specs || ""}
                onChange={(e) => setFormData({ ...formData, specs: e.target.value })}
                className="w-full px-4 py-3 bg-black border border-white/20 rounded-lg text-foreground focus:border-primary focus:ring-1 focus:ring-primary outline-none"
                required
                data-testid="form-specs"
              />
            </div>
            <div>
              <label className="block text-sm text-muted-foreground mb-2">Category</label>
              <select
                value={formData.category || ""}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full px-4 py-3 bg-black border border-white/20 rounded-lg text-foreground focus:border-primary focus:ring-1 focus:ring-primary outline-none"
                required
                data-testid="form-category"
              >
                <option value="">Select Category</option>
                <option value="Indoor">Indoor</option>
                <option value="Outdoor">Outdoor</option>
                <option value="Rental">Rental</option>
              </select>
            </div>
            <ImageUpload
              label="Product Image"
              value={formData.image}
              onChange={(url) => setFormData({ ...formData, image: url })}
            />
            <div className="flex gap-3">
              <button
                type="submit"
                className="px-6 py-2 bg-primary text-black font-semibold rounded-lg hover:bg-primary/90 transition-colors flex items-center gap-2"
                data-testid="form-submit-btn"
              >
                <Save size={18} /> {editingId ? "Update" : "Save"}
              </button>
              <button type="button" onClick={resetForm} className="px-6 py-2 bg-white/10 text-foreground rounded-lg hover:bg-white/20 transition-colors">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <p className="text-muted-foreground">Loading...</p>
      ) : items.length === 0 ? (
        <p className="text-muted-foreground">No products found. Add your first one!</p>
      ) : (
        <div className="space-y-4">
          {items.map((item) => (
            <div key={item.id} className="bg-card p-4 rounded-lg border border-white/10 flex items-center justify-between gap-4">
              <div className="flex items-center gap-4 flex-1 min-w-0">
                <img src={item.image} alt={item.name} className="w-16 h-16 object-cover rounded" />
                <div>
                  <p className="font-semibold">{item.name}</p>
                  <p className="text-sm text-muted-foreground">{item.specs}</p>
                  <span className="text-xs text-primary">{item.category}</span>
                </div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => handleEdit(item)} className="p-2 text-primary hover:bg-primary/10 rounded-lg transition-colors" data-testid={`edit-${item.id}`}>
                  <Edit size={18} />
                </button>
                <button onClick={() => handleDelete(item.id)} className="p-2 text-red-400 hover:bg-red-500/10 rounded-lg transition-colors" data-testid={`delete-${item.id}`}>
                  <Trash2 size={18} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const AdminBlogs = () => (
  <AdminCRUD
    title="Blog Posts"
    endpoint="blogs"
    fields={[
      { name: "title", label: "Title", required: true },
      { name: "excerpt", label: "Excerpt", type: "textarea", required: true },
      { name: "date", label: "Date (YYYY-MM-DD)", required: true },
    ]}
    renderItem={(item) => (
      <div>
        <p className="font-semibold">{item.title}</p>
        <p className="text-sm text-muted-foreground line-clamp-2">{item.excerpt}</p>
        <span className="text-xs text-primary font-mono">{item.date}</span>
      </div>
    )}
  />
);

const AdminGallery = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({});
  const [showForm, setShowForm] = useState(false);

  const fetchItems = async () => {
    try {
      const res = await authAxios.get(`${API}/admin/gallery`);
      setItems(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchItems(); }, []);

  const resetForm = () => {
    setFormData({});
    setEditingId(null);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await authAxios.put(`${API}/admin/gallery/${editingId}`, formData);
      } else {
        await authAxios.post(`${API}/admin/gallery`, formData);
      }
      fetchItems();
      resetForm();
    } catch (e) {
      console.error(e);
      alert("Error saving gallery item");
    }
  };

  const handleEdit = (item) => {
    setFormData(item);
    setEditingId(item.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this gallery item?")) return;
    try {
      await authAxios.delete(`${API}/admin/gallery/${id}`);
      fetchItems();
    } catch (e) {
      console.error(e);
      alert("Error deleting gallery item");
    }
  };

  return (
    <div data-testid="admin-gallery">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-unbounded text-2xl font-bold">Gallery</h1>
        <button
          onClick={() => { resetForm(); setShowForm(true); }}
          className="px-4 py-2 bg-primary text-black font-semibold rounded-lg hover:bg-primary/90 transition-colors flex items-center gap-2"
          data-testid="add-gallery-btn"
        >
          <Plus size={18} /> Add New
        </button>
      </div>

      {showForm && (
        <div className="mb-8 bg-card p-6 rounded-lg border border-white/10">
          <h2 className="font-unbounded text-lg font-semibold mb-4">{editingId ? "Edit" : "Add"} Gallery Item</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm text-muted-foreground mb-2">Title</label>
              <input
                type="text"
                value={formData.title || ""}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full px-4 py-3 bg-black border border-white/20 rounded-lg text-foreground focus:border-primary focus:ring-1 focus:ring-primary outline-none"
                required
                data-testid="form-title"
              />
            </div>
            <ImageUpload
              label="Gallery Image"
              value={formData.url}
              onChange={(url) => setFormData({ ...formData, url: url })}
            />
            <div>
              <label className="block text-sm text-muted-foreground mb-2">Grid Span (optional)</label>
              <select
                value={formData.span || ""}
                onChange={(e) => setFormData({ ...formData, span: e.target.value })}
                className="w-full px-4 py-3 bg-black border border-white/20 rounded-lg text-foreground focus:border-primary focus:ring-1 focus:ring-primary outline-none"
                data-testid="form-span"
              >
                <option value="">Normal size</option>
                <option value="md:col-span-2">Wide (2 columns)</option>
              </select>
            </div>
            <div className="flex gap-3">
              <button
                type="submit"
                className="px-6 py-2 bg-primary text-black font-semibold rounded-lg hover:bg-primary/90 transition-colors flex items-center gap-2"
                data-testid="form-submit-btn"
              >
                <Save size={18} /> {editingId ? "Update" : "Save"}
              </button>
              <button type="button" onClick={resetForm} className="px-6 py-2 bg-white/10 text-foreground rounded-lg hover:bg-white/20 transition-colors">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <p className="text-muted-foreground">Loading...</p>
      ) : items.length === 0 ? (
        <p className="text-muted-foreground">No gallery items found. Add your first one!</p>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {items.map((item) => (
            <div key={item.id} className="bg-card rounded-lg border border-white/10 overflow-hidden group">
              <div className="relative h-32">
                <img src={item.url} alt={item.title} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <button onClick={() => handleEdit(item)} className="p-2 bg-primary text-black rounded-lg" data-testid={`edit-${item.id}`}>
                    <Edit size={16} />
                  </button>
                  <button onClick={() => handleDelete(item.id)} className="p-2 bg-red-500 text-white rounded-lg" data-testid={`delete-${item.id}`}>
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
              <div className="p-3">
                <p className="text-sm font-semibold truncate">{item.title}</p>
                {item.span && <span className="text-xs text-primary">Wide</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const AdminFAQs = () => (
  <AdminCRUD
    title="FAQs"
    endpoint="faqs"
    fields={[
      { name: "question", label: "Question", required: true },
      { name: "answer", label: "Answer", type: "textarea", required: true },
    ]}
    renderItem={(item) => (
      <div>
        <p className="font-semibold">{item.question}</p>
        <p className="text-sm text-muted-foreground line-clamp-2">{item.answer}</p>
      </div>
    )}
  />
);

const AdminMessages = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMessages = async () => {
      try {
        const res = await authAxios.get(`${API}/admin/messages`);
        setMessages(res.data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchMessages();
  }, []);

  return (
    <div data-testid="admin-messages">
      <h1 className="font-unbounded text-2xl font-bold mb-8">Messages</h1>
      {loading ? (
        <p className="text-muted-foreground">Loading...</p>
      ) : messages.length === 0 ? (
        <p className="text-muted-foreground">No messages yet.</p>
      ) : (
        <div className="space-y-4">
          {messages.map((msg) => (
            <div key={msg.id} className="bg-card p-6 rounded-lg border border-white/10">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <p className="font-semibold">{msg.name}</p>
                  <p className="text-sm text-primary">{msg.email}</p>
                </div>
                <span className="text-xs text-muted-foreground font-mono">{msg.created_at?.split("T")[0]}</span>
              </div>
              <p className="text-muted-foreground">{msg.message}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ============ PUBLIC WEBSITE COMPONENTS ============

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const navItems = [
    { name: "HOME", href: "#home" },
    { name: "SERVICES", href: "#services" },
    { name: "GALLERY", href: "#gallery" },
    { name: "INSTALLATION", href: "#installation" },
    { name: "AUDIO", href: "#audio" },
    { name: "EVENTS", href: "#events" },
    { name: "ABOUT", href: "#about" },
    { name: "BLOG", href: "#blog" },
    { name: "FAQ", href: "#faq" },
    { name: "CONTACT", href: "#contact" },
    { name: "PRODUCTS", href: "#products" },
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass" data-testid="navigation">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          <a href="#home" className="flex items-center gap-4" data-testid="logo">
            <span className="font-unbounded font-bold text-xl text-primary tracking-wider">
              UPCLOSE DIGITAL
            </span>
          </a>
          <span className="hidden lg:block text-muted-foreground text-sm font-mono">
            upclose-digital.com
          </span>
          <div className="hidden lg:flex items-center gap-8">
            {navItems.map((item) => (
              <a
                key={item.name}
                href={item.href}
                className="text-xs tracking-[0.15em] text-foreground/80 hover:text-primary transition-colors duration-300"
                data-testid={`nav-${item.name.toLowerCase()}`}
              >
                {item.name}
              </a>
            ))}
          </div>
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden text-foreground p-2"
            data-testid="mobile-menu-btn"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
        {isOpen && (
          <div className="lg:hidden pb-6 border-t border-white/10 mt-4 pt-4" data-testid="mobile-menu">
            {navItems.map((item) => (
              <a
                key={item.name}
                href={item.href}
                onClick={() => setIsOpen(false)}
                className="block py-3 text-sm tracking-[0.15em] text-foreground/80 hover:text-primary transition-colors"
              >
                {item.name}
              </a>
            ))}
          </div>
        )}
      </div>
    </nav>
  );
};

const HeroSection = () => {
  return (
    <section 
      id="home" 
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      data-testid="hero-section"
    >
      <div 
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: `url('/ai_hero_background.png')`
        }}
      />
      <div className="absolute inset-0 bg-black/60" />
      <div className="absolute top-20 left-0 right-0 z-20 bg-black/80 py-3 border-y border-white/10">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <span className="text-sm text-muted-foreground">
            Visit our main website for more information:{" "}
            <a href="https://upclose-digital.com" className="text-primary underline hover:text-primary/80 transition-colors" target="_blank" rel="noopener noreferrer">
              UPCLOSE-DIGITAL.COM <ArrowRight className="inline w-4 h-4" />
            </a>
          </span>
        </div>
      </div>
      <div className="relative z-10 text-center px-6 max-w-5xl mx-auto pt-16">
        <span className="inline-block text-xs tracking-[0.3em] text-primary mb-6 font-mono">
          LED SCREEN EXCELLENCE
        </span>
        <h1 className="font-unbounded text-5xl md:text-7xl lg:text-8xl font-black uppercase mb-6 leading-tight">
          <span className="text-glow-cyan text-primary">ILLUMINATE</span>
          <br />
          <span className="text-foreground">YOUR VISION</span>
        </h1>
        <p className="text-lg md:text-xl text-foreground/70 max-w-2xl mx-auto mb-10 font-outfit">
          Leading manufacturer and supplier of premium LED display solutions. From manufacturing to installation and maintenance, we deliver cutting-edge visual technology that transforms spaces.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <a href="#services" className="px-8 py-4 bg-primary text-black font-semibold text-sm tracking-wider uppercase rounded-full glow-box-cyan hover:bg-primary/90 transition-all duration-300 flex items-center justify-center gap-2" data-testid="explore-solutions-btn">
            EXPLORE SOLUTIONS <ChevronRight size={18} />
          </a>
          <a href="#products" className="px-8 py-4 border border-secondary text-secondary font-semibold text-sm tracking-wider uppercase rounded-full hover:bg-secondary/10 transition-all duration-300 flex items-center justify-center gap-2" data-testid="view-products-btn">
            VIEW PRODUCTS <ChevronRight size={18} />
          </a>
          <a href="#contact" className="px-8 py-4 border border-white/20 text-foreground font-semibold text-sm tracking-wider uppercase rounded-full hover:bg-white/5 transition-all duration-300" data-testid="get-in-touch-btn">
            GET IN TOUCH
          </a>
        </div>
      </div>
    </section>
  );
};

const ServicesSection = () => {
  const services = [
    { icon: <Monitor className="w-10 h-10" />, title: "Indoor LED Displays", description: "High-resolution indoor LED screens perfect for corporate environments, retail spaces, and entertainment venues." },
    { icon: <Tv className="w-10 h-10" />, title: "Outdoor LED Displays", description: "Weather-resistant outdoor LED solutions for billboards, stadiums, and large-scale advertising." },
    { icon: <Settings className="w-10 h-10" />, title: "LED Rental Services", description: "Flexible rental options for events, concerts, exhibitions, and temporary installations." },
    { icon: <Wrench className="w-10 h-10" />, title: "Installation & Maintenance", description: "Professional installation and ongoing maintenance services to ensure optimal performance." },
  ];

  return (
    <section id="services" className="py-24 md:py-32 bg-background" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-16">
          <span className="text-xs tracking-[0.3em] text-primary font-mono">WHAT WE OFFER</span>
          <h2 className="font-unbounded text-4xl md:text-5xl font-bold uppercase mt-4">Our Services</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {services.map((service, index) => (
            <div key={index} className="group p-8 bg-card rounded-lg border border-white/10 hover:border-primary transition-all duration-300 hover:glow-box-cyan" data-testid={`service-card-${index}`}>
              <div className="text-primary mb-6 group-hover:text-glow-cyan transition-all">{service.icon}</div>
              <h3 className="font-unbounded text-xl font-semibold mb-4">{service.title}</h3>
              <p className="text-muted-foreground leading-relaxed">{service.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const ProductsSection = () => {
  const [products, setProducts] = useState([]);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const res = await axios.get(`${API}/products`);
        setProducts(res.data);
      } catch (e) {
        console.error(e);
      }
    };
    fetchProducts();
  }, []);

  return (
    <section id="products" className="py-24 md:py-32 bg-muted" data-testid="products-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-16">
          <span className="text-xs tracking-[0.3em] text-primary font-mono">OUR PRODUCTS</span>
          <h2 className="font-unbounded text-4xl md:text-5xl font-bold uppercase mt-4">LED Solutions</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {products.map((product, index) => (
            <div key={product.id} className="group bg-card rounded-lg overflow-hidden border border-white/10 hover:border-primary transition-all duration-300" data-testid={`product-card-${index}`}>
              <div className="relative h-48 overflow-hidden">
                <img src={product.image} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                <span className="absolute top-4 left-4 px-3 py-1 bg-primary/90 text-black text-xs font-mono rounded">{product.category}</span>
              </div>
              <div className="p-6">
                <h3 className="font-unbounded text-lg font-semibold mb-2">{product.name}</h3>
                <p className="text-muted-foreground text-sm font-mono mb-4">{product.specs}</p>
                <button className="text-primary text-sm flex items-center gap-2 hover:gap-3 transition-all">View Details <ChevronRight size={16} /></button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const GallerySection = () => {
  const [gallery, setGallery] = useState([]);

  useEffect(() => {
    const fetchGallery = async () => {
      try {
        const res = await axios.get(`${API}/gallery`);
        setGallery(res.data);
      } catch (e) {
        console.error(e);
      }
    };
    fetchGallery();
  }, []);

  return (
    <section id="gallery" className="py-24 md:py-32 bg-background" data-testid="gallery-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-16">
          <span className="text-xs tracking-[0.3em] text-primary font-mono">OUR WORK</span>
          <h2 className="font-unbounded text-4xl md:text-5xl font-bold uppercase mt-4">Project Gallery</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {gallery.map((item, index) => (
            <div key={item.id} className={`group relative h-64 md:h-80 overflow-hidden rounded-lg ${item.span}`} data-testid={`gallery-item-${index}`}>
              <img src={item.url} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="absolute bottom-4 left-4">
                  <h4 className="font-unbounded text-lg font-semibold">{item.title}</h4>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Permanent Installation Section - Permanent LED Supply & Installation
const PermanentInstallationSection = () => {
  const installations = [
    {
      url: "/generated_corporate_lobby_led.png",
      title: "Corporate Lobby LED Wall",
      description: "LED screen installation in corporate building lobby",
      span: "md:col-span-2"
    },
    {
      url: "/generated_hotel_video_wall.png",
      title: "Hotel Video Wall",
      description: "LED video wall in luxury hotel lounge",
      span: ""
    },
    {
      url: "/generated_indoor_led_display.png",
      title: "Retail LED Display",
      description: "High quality LED display for retail showroom",
      span: ""
    },
    {
      url: "/generated_meeting_room_led.png",
      title: "Meeting Room LED Wall",
      description: "Permanent installation in meeting room",
      span: ""
    },
    {
      url: "/generated_shopping_mall_led.png",
      title: "Shopping Mall Digital Signage",
      description: "LED screen in modern shopping mall",
      span: "md:col-span-2"
    },
    {
      url: "/generated_event_stage_led.png",
      title: "Event Stage LED Display",
      description: "LED screen installation for events",
      span: ""
    },
  ];

  const features = [
    { title: "Professional Installation", desc: "Experienced team for safe and neat installation" },
    { title: "2 Year Warranty", desc: "Quality guarantee for your peace of mind" },
    { title: "24/7 Technical Support", desc: "Technical assistance whenever you need it" },
    { title: "Regular Maintenance", desc: "Maintenance service for optimal performance" },
  ];

  return (
    <section id="installation" className="py-24 md:py-32 bg-muted" data-testid="installation-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="text-xs tracking-[0.3em] text-primary font-mono">PERMANENT INSTALLATION</span>
          <h2 className="font-unbounded text-4xl md:text-5xl font-bold uppercase mt-4">
            LED Supply & Installation
          </h2>
          <p className="text-muted-foreground mt-4 max-w-2xl mx-auto">
            We provide permanent LED screen supply and installation services for commercial buildings, offices, hotels, and shopping malls.
          </p>
        </div>

        {/* Gallery Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-16">
          {installations.map((item, index) => (
            <div 
              key={index} 
              className={`group relative h-64 md:h-80 overflow-hidden rounded-lg ${item.span}`}
              data-testid={`installation-item-${index}`}
            >
              <img 
                src={item.url} 
                alt={item.title} 
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" 
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent">
                <div className="absolute bottom-4 left-4 right-4">
                  <h4 className="font-unbounded text-lg font-semibold text-white">{item.title}</h4>
                  <p className="text-white/70 text-sm mt-1">{item.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Features */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((feature, index) => (
            <div 
              key={index} 
              className="p-6 bg-card rounded-lg border border-white/10 hover:border-primary transition-colors"
            >
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                <span className="text-primary font-unbounded font-bold text-xl">{index + 1}</span>
              </div>
              <h4 className="font-unbounded font-semibold mb-2">{feature.title}</h4>
              <p className="text-muted-foreground text-sm">{feature.desc}</p>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-16 text-center">
          <p className="text-muted-foreground mb-6">Interested in permanent LED installation for your building?</p>
          <a 
            href="#contact" 
            className="inline-flex items-center gap-2 px-8 py-4 bg-primary text-black font-semibold text-sm tracking-wider uppercase rounded-full glow-box-cyan hover:bg-primary/90 transition-all duration-300"
          >
            Get a Quote <ChevronRight size={18} />
          </a>
        </div>
      </div>
    </section>
  );
};

// Audio System Rental Section
const AudioSystemSection = () => {
  const audioEquipment = [
    {
      url: "/generated_line_array_speaker.png",
      title: "Line Array Speaker",
      description: "Line array speaker system for large events",
      span: "md:col-span-2"
    },
    {
      url: "/generated_stage_speakers.png",
      title: "Professional PA System",
      description: "High quality speakers for crystal clear sound",
      span: ""
    },
    {
      url: "/generated_audio_mixer.png",
      title: "Digital Audio Mixer",
      description: "Professional digital audio mixer console",
      span: ""
    },
    {
      url: "/ai_audio_microphone.png",
      title: "Professional Microphone",
      description: "High quality microphone for vocals",
      span: ""
    },
    {
      url: "/ai_audio_mixer.png",
      title: "Studio Equipment",
      description: "Studio mixer for recording and live events",
      span: "md:col-span-2"
    },
  ];

  const packages = [
    {
      name: "Basic Package",
      description: "Suitable for small events (50-100 pax)",
      items: ["2x Active Speakers", "1x 8 Channel Mixer", "2x Wireless Microphones", "Cables & Stands"],
      highlight: false
    },
    {
      name: "Standard Package",
      description: "Suitable for medium events (100-300 pax)",
      items: ["4x Line Array Speakers", "1x 16 Channel Digital Mixer", "4x Wireless Microphones", "2x Monitor Speakers", "Subwoofer"],
      highlight: true
    },
    {
      name: "Premium Package",
      description: "Suitable for large events (300+ pax)",
      items: ["8x Line Array Speakers", "1x 32 Channel Digital Console", "8x Wireless Microphones", "4x Monitor Speakers", "4x Subwoofers", "Sound Engineer"],
      highlight: false
    },
  ];

  return (
    <section id="audio" className="py-24 md:py-32 bg-background" data-testid="audio-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="text-xs tracking-[0.3em] text-secondary font-mono">AUDIO RENTAL</span>
          <h2 className="font-unbounded text-4xl md:text-5xl font-bold uppercase mt-4">
            Audio System Rental
          </h2>
          <p className="text-muted-foreground mt-4 max-w-2xl mx-auto">
            Professional audio system rental services for various events - weddings, concerts, seminars, and corporate events.
          </p>
        </div>

        {/* Equipment Gallery */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-16">
          {audioEquipment.map((item, index) => (
            <div 
              key={index} 
              className={`group relative h-56 md:h-64 overflow-hidden rounded-lg ${item.span}`}
              data-testid={`audio-item-${index}`}
            >
              <img 
                src={item.url} 
                alt={item.title} 
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" 
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent">
                <div className="absolute bottom-4 left-4 right-4">
                  <h4 className="font-unbounded text-lg font-semibold text-white">{item.title}</h4>
                  <p className="text-white/70 text-sm mt-1">{item.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Packages */}
        <div className="mb-16">
          <h3 className="font-unbounded text-2xl font-bold text-center mb-8">Audio Rental Packages</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {packages.map((pkg, index) => (
              <div 
                key={index}
                className={`p-6 rounded-lg border ${pkg.highlight ? 'bg-secondary/10 border-secondary glow-box-purple' : 'bg-card border-white/10'}`}
              >
                <h4 className="font-unbounded text-xl font-semibold mb-2">{pkg.name}</h4>
                <p className="text-muted-foreground text-sm mb-4">{pkg.description}</p>
                <ul className="space-y-2">
                  {pkg.items.map((item, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-sm">
                      <span className="w-1.5 h-1.5 bg-primary rounded-full"></span>
                      {item}
                    </li>
                  ))}
                </ul>
                {pkg.highlight && (
                  <span className="inline-block mt-4 px-3 py-1 bg-secondary text-white text-xs rounded-full">
                    Popular
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center p-8 bg-card rounded-lg border border-white/10">
          <h3 className="font-unbounded text-xl font-semibold mb-2">Need Audio System for Your Event?</h3>
          <p className="text-muted-foreground mb-6">Contact us for customized packages tailored to your needs.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="#contact" 
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-secondary text-white font-semibold text-sm tracking-wider uppercase rounded-full glow-box-purple hover:bg-secondary/90 transition-all duration-300"
            >
              Get a Quote <ChevronRight size={18} />
            </a>
            <a 
              href="https://wa.me/60197866222" 
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 border border-white/20 text-foreground font-semibold text-sm tracking-wider uppercase rounded-full hover:bg-white/5 transition-all duration-300"
            >
              <MessageCircle size={18} /> WhatsApp Us
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

// Event Management Services Section
const EventManagementSection = () => {
  const [activeVideo, setActiveVideo] = useState(0);

  const events = [
    {
      video: "/event_concert.mp4",
      title: "Concert & Live Shows",
      description: "Full-scale LED screen setup for concerts and live performances. Massive visual impact with synchronized lighting and sound.",
      features: ["Large Format Screens", "Stage Production", "Live VJ Visuals", "Full AV Setup"]
    },
    {
      video: "/event_corporate.mp4",
      title: "Corporate Events",
      description: "Professional event production for conferences, AGMs, town halls, and corporate launches with premium LED displays.",
      features: ["Presentation Screens", "Stage Design", "Live Streaming Setup", "Professional AV System"]
    },
    {
      video: "/event_csr.mp4",
      title: "CSR Events",
      description: "Community-focused event production with LED screens for CSR programs, charity events, and public engagement activities.",
      features: ["Outdoor LED Setup", "Event Branding", "Community Engagement", "Photo & Video Coverage"]
    },
    {
      video: "/event_dinner.mp4",
      title: "Gala Dinner & Awards",
      description: "Elegant event production for gala dinners, award ceremonies, and formal celebrations with stunning LED visual displays.",
      features: ["LED Backdrop Design", "Ambient Lighting", "Table Projection Mapping", "Award Show Production"]
    },
    {
      video: "/event_gimmick.mp4",
      title: "Product Launch & Gimmick",
      description: "Dramatic product launch ceremonies with countdown displays, special effects, confetti, and pyrotechnics on LED screens.",
      features: ["Launch Countdown", "Special Effects", "Confetti & Pyrotechnics", "Press Conference Setup"]
    },
    {
      video: "/event_led_backdrop.mp4",
      title: "Giant LED Backdrop",
      description: "Massive LED video walls as stage backdrops for events of any scale. Custom content display with ultra-high resolution visuals.",
      features: ["Custom Sizing", "Ultra HD Content", "Seamless Panels", "Real-time Graphics"]
    },
    {
      video: "/event_stage_lighting.mp4",
      title: "Stage Lighting Design",
      description: "Professional intelligent lighting design with moving heads, beam effects, lasers, and synchronized light shows for maximum impact.",
      features: ["Moving Head Lights", "Beam & Laser Effects", "DMX Programming", "Synchronized Shows"]
    },
    {
      video: "/event_visual_mixing.mp4",
      title: "Live Visual Mixing",
      description: "Real-time VJ visual mixing and content creation for LED screens. Dynamic visuals synchronized with music and event flow.",
      features: ["VJ Performance", "Real-time Graphics", "Content Creation", "Multi-screen Sync"]
    },
    {
      video: "/event_sound_system.mp4",
      title: "Sound System Integration",
      description: "Complete professional audio solutions with line array speakers, subwoofers, mixers, and wireless microphone systems.",
      features: ["Line Array Speakers", "Digital Audio Mixer", "Wireless Mics", "Stage Monitors"]
    },
    {
      video: "/event_custom_sizing.mp4",
      title: "Custom Sizing",
      description: "Custom-built LED displays tailored to your exact specifications. Any size, any shape, any resolution — we build it to fit your vision.",
      features: ["Modular Assembly", "Any Dimension", "Curved & Flat Options", "On-site Fabrication"]
    },
    {
      video: "/event_ultra_hd.mp4",
      title: "Ultra HD Content",
      description: "Crystal-clear 4K and 8K content playback on our ultra-fine pitch LED displays. Every pixel delivers stunning color accuracy and detail.",
      features: ["4K/8K Playback", "HDR Support", "Color Calibration", "Content Mastering"]
    },
    {
      video: "/event_seamless_panels.mp4",
      title: "Seamless Panels",
      description: "Edge-to-edge seamless LED panel technology with zero visible gaps. Multiple modules unite into one continuous display surface.",
      features: ["Zero-gap Design", "Perfect Alignment", "Uniform Brightness", "Invisible Joints"]
    },
    {
      video: "/event_realtime_graphics.mp4",
      title: "Real-time Graphics",
      description: "Live generative graphics, data visualization, and dynamic content rendered in real-time on LED displays for immersive experiences.",
      features: ["Motion Graphics", "Data Visualization", "Generative Art", "Interactive Content"]
    },
  ];

  return (
    <section id="events" className="py-24 md:py-32 bg-muted" data-testid="events-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="text-xs tracking-[0.3em] text-primary font-mono">EVENT MANAGEMENT</span>
          <h2 className="font-unbounded text-4xl md:text-5xl font-bold uppercase mt-4">
            Event Management Services
          </h2>
          <p className="text-muted-foreground mt-4 max-w-2xl mx-auto">
            Complete event production and management services powered by cutting-edge LED technology. From concerts to corporate events, we deliver unforgettable experiences.
          </p>
        </div>

        {/* Featured Video */}
        <div className="mb-12">
          <div className="relative rounded-2xl overflow-hidden border border-white/10 aspect-video max-w-4xl mx-auto">
            <video
              key={events[activeVideo].video}
              src={events[activeVideo].video}
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
              className="w-full h-full object-cover"
              data-testid="event-featured-video"
              onCanPlay={(e) => e.target.play().catch(() => {})}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-5 h-5 text-primary" />
                <span className="text-primary text-sm font-mono tracking-wider">FEATURED SERVICE</span>
              </div>
              <h3 className="font-unbounded text-2xl md:text-3xl font-bold text-white mb-2">
                {events[activeVideo].title}
              </h3>
              <p className="text-white/70 max-w-xl">
                {events[activeVideo].description}
              </p>
            </div>
          </div>
        </div>

        {/* Event Cards Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-16">
          {events.map((event, index) => (
            <button
              key={index}
              onClick={() => setActiveVideo(index)}
              className={`group relative rounded-xl overflow-hidden border transition-all duration-300 text-left ${
                activeVideo === index
                  ? "border-primary ring-2 ring-primary/30 scale-[1.02]"
                  : "border-white/10 hover:border-white/30"
              }`}
              data-testid={`event-card-${index}`}
            >
              <div className="relative h-32">
                <video
                  src={event.video}
                  muted
                  loop
                  playsInline
                  className="w-full h-full object-cover"
                  onMouseEnter={(e) => e.target.play()}
                  onMouseLeave={(e) => { e.target.pause(); e.target.currentTime = 0; }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 to-black/20" />
                {activeVideo === index && (
                  <div className="absolute top-2 right-2 w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                    <Play className="w-3 h-3 text-black fill-black" />
                  </div>
                )}
              </div>
              <div className="p-3 bg-card">
                <h4 className="font-unbounded text-xs font-semibold truncate">{event.title}</h4>
              </div>
            </button>
          ))}
        </div>

        {/* Features of Active Event */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-16">
          {events[activeVideo].features.map((feature, index) => (
            <div
              key={index}
              className="p-5 bg-card rounded-lg border border-white/10 hover:border-primary/50 transition-colors text-center"
              data-testid={`event-feature-${index}`}
            >
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center mx-auto mb-3">
                <Calendar className="w-5 h-5 text-primary" />
              </div>
              <p className="text-sm font-semibold">{feature}</p>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center p-8 bg-card rounded-lg border border-white/10">
          <h3 className="font-unbounded text-xl font-semibold mb-2">Planning an Event?</h3>
          <p className="text-muted-foreground mb-6">Let us handle the LED screens, lighting, sound, and production for your next event.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="#contact"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-primary text-black font-semibold text-sm tracking-wider uppercase rounded-full glow-box-cyan hover:bg-primary/90 transition-all duration-300"
              data-testid="events-cta-btn"
            >
              Get a Quote <ChevronRight size={18} />
            </a>
            <a
              href="https://wa.me/60197866222"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 border border-white/20 text-foreground font-semibold text-sm tracking-wider uppercase rounded-full hover:bg-white/5 transition-all duration-300"
              data-testid="events-whatsapp-btn"
            >
              <MessageCircle size={18} /> WhatsApp Us
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

const AboutSection = () => {
  return (
    <section id="about" className="py-24 md:py-32 bg-muted" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="text-xs tracking-[0.3em] text-primary font-mono">ABOUT US</span>
            <h2 className="font-unbounded text-4xl md:text-5xl font-bold uppercase mt-4 mb-6">Who We Are</h2>
            <p className="text-muted-foreground text-lg leading-relaxed mb-6">
              UpClose Digital is a leading provider of premium LED display solutions in Malaysia. With years of experience in the industry, we specialize in manufacturing, supplying, installing, and maintaining cutting-edge LED technology.
            </p>
            <p className="text-muted-foreground text-lg leading-relaxed mb-8">
              Our commitment to quality and innovation has made us the trusted partner for businesses seeking to illuminate their vision with stunning visual displays.
            </p>
            <div className="flex gap-8">
              <div>
                <span className="font-unbounded text-4xl font-bold text-primary">10+</span>
                <p className="text-muted-foreground text-sm mt-1">Years Experience</p>
              </div>
              <div>
                <span className="font-unbounded text-4xl font-bold text-primary">500+</span>
                <p className="text-muted-foreground text-sm mt-1">Projects Completed</p>
              </div>
              <div>
                <span className="font-unbounded text-4xl font-bold text-primary">500+</span>
                <p className="text-muted-foreground text-sm mt-1">Happy Clients</p>
              </div>
            </div>
          </div>
          <div className="relative">
            <div className="relative overflow-hidden rounded-lg">
              <img src="/ai_about_company.png" alt="About UpClose Digital" className="w-full h-[400px] object-cover relative z-10" />
            </div>
            <div className="absolute -bottom-4 -right-4 w-full h-full border-2 border-primary rounded-lg" />
          </div>
        </div>
      </div>
    </section>
  );
};

const BlogSection = () => {
  const [blogs, setBlogs] = useState([]);

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        const res = await axios.get(`${API}/blogs`);
        setBlogs(res.data);
      } catch (e) {
        console.error(e);
      }
    };
    fetchBlogs();
  }, []);

  return (
    <section id="blog" className="py-24 md:py-32 bg-background" data-testid="blog-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-16">
          <span className="text-xs tracking-[0.3em] text-primary font-mono">LATEST NEWS</span>
          <h2 className="font-unbounded text-4xl md:text-5xl font-bold uppercase mt-4">Our Blog</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {blogs.map((post, index) => (
            <article key={post.id} className="group bg-transparent border-t-2 border-transparent hover:border-primary p-6 transition-all duration-300" data-testid={`blog-post-${index}`}>
              <time className="text-xs font-mono text-muted-foreground">{post.date}</time>
              <h3 className="font-unbounded text-xl font-semibold mt-3 mb-3 group-hover:text-primary transition-colors">{post.title}</h3>
              <p className="text-muted-foreground leading-relaxed mb-4">{post.excerpt}</p>
              <a href="#" className="text-primary text-sm flex items-center gap-2 hover:gap-3 transition-all">Read More <ChevronRight size={16} /></a>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

const FAQSection = () => {
  const [faqs, setFaqs] = useState([]);

  useEffect(() => {
    const fetchFaqs = async () => {
      try {
        const res = await axios.get(`${API}/faqs`);
        setFaqs(res.data);
      } catch (e) {
        console.error(e);
      }
    };
    fetchFaqs();
  }, []);

  return (
    <section id="faq" className="py-24 md:py-32 bg-muted" data-testid="faq-section">
      <div className="max-w-3xl mx-auto px-6 md:px-12">
        <div className="text-center mb-16">
          <span className="text-xs tracking-[0.3em] text-primary font-mono">FAQ</span>
          <h2 className="font-unbounded text-4xl md:text-5xl font-bold uppercase mt-4">Common Questions</h2>
        </div>
        <Accordion type="single" collapsible className="space-y-4">
          {faqs.map((faq, index) => (
            <AccordionItem key={faq.id} value={`item-${index}`} className="border border-white/10 rounded-lg px-6 data-[state=open]:border-primary transition-colors" data-testid={`faq-item-${index}`}>
              <AccordionTrigger className="text-left font-outfit text-lg hover:text-primary transition-colors py-6">{faq.question}</AccordionTrigger>
              <AccordionContent className="text-muted-foreground pb-6">{faq.answer}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
};

const ContactSection = () => {
  const [formData, setFormData] = useState({ name: "", email: "", message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitStatus("success");
      setFormData({ name: "", email: "", message: "" });
    } catch (error) {
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubmitStatus(null), 3000);
    }
  };

  return (
    <section id="contact" className="py-24 md:py-32 bg-background" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-16">
          <span className="text-xs tracking-[0.3em] text-primary font-mono">GET IN TOUCH</span>
          <h2 className="font-unbounded text-4xl md:text-5xl font-bold uppercase mt-4">Let's Talk</h2>
          <p className="text-muted-foreground mt-4">Have a project in mind? We'd love to hear from you.</p>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div className="space-y-8">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-primary/10 rounded-lg"><Mail className="w-6 h-6 text-primary" /></div>
              <div>
                <h4 className="font-unbounded font-semibold text-lg">Email</h4>
                <a href="mailto:marketing@upclose-digital.com" className="text-muted-foreground hover:text-primary transition-colors">marketing@upclose-digital.com</a>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="p-3 bg-primary/10 rounded-lg"><Phone className="w-6 h-6 text-primary" /></div>
              <div>
                <h4 className="font-unbounded font-semibold text-lg">Phone</h4>
                <a href="tel:+60197866222" className="text-muted-foreground hover:text-primary transition-colors">+60 19-786 6222</a>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="p-3 bg-primary/10 rounded-lg"><MapPin className="w-6 h-6 text-primary" /></div>
              <div>
                <h4 className="font-unbounded font-semibold text-lg">Website</h4>
                <a href="https://upclose-digital.com" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-primary transition-colors">upclose-digital.com</a>
              </div>
            </div>
            <div className="p-6 bg-card rounded-lg border border-white/10 mt-8">
              <div className="flex items-center gap-3 mb-2">
                <User className="w-5 h-5 text-primary" />
                <h4 className="font-unbounded font-semibold">Contact Person</h4>
              </div>
              <p className="text-foreground text-lg">Merzuki Noor</p>
            </div>
          </div>
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
            <div>
              <input type="text" placeholder="Your Name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required className="w-full px-4 py-4 bg-black border border-white/20 rounded-lg text-foreground placeholder:text-muted-foreground focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-all" data-testid="contact-name-input" />
            </div>
            <div>
              <input type="email" placeholder="Your Email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required className="w-full px-4 py-4 bg-black border border-white/20 rounded-lg text-foreground placeholder:text-muted-foreground focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-all" data-testid="contact-email-input" />
            </div>
            <div>
              <textarea placeholder="Your Message" rows={5} value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })} required className="w-full px-4 py-4 bg-black border border-white/20 rounded-lg text-foreground placeholder:text-muted-foreground focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-all resize-none" data-testid="contact-message-input" />
            </div>
            <button type="submit" disabled={isSubmitting} className="px-8 py-4 bg-primary text-black font-semibold text-sm tracking-wider uppercase rounded-lg glow-box-cyan hover:bg-primary/90 transition-all duration-300 disabled:opacity-50" data-testid="contact-submit-btn">
              {isSubmitting ? "SENDING..." : "SEND MESSAGE"}
            </button>
            {submitStatus === "success" && <p className="text-accent text-sm">Message sent successfully!</p>}
            {submitStatus === "error" && <p className="text-red-500 text-sm">Failed to send message. Please try again.</p>}
          </form>
        </div>
      </div>
    </section>
  );
};

const Footer = () => {
  const quickLinks = [
    { name: "Home", href: "#home" },
    { name: "Services", href: "#services" },
    { name: "Products", href: "#products" },
    { name: "Gallery", href: "#gallery" },
  ];
  const otherLinks = [
    { name: "About Us", href: "#about" },
    { name: "Blog", href: "#blog" },
    { name: "FAQ", href: "#faq" },
    { name: "Contact", href: "#contact" },
  ];

  return (
    <footer className="py-24 bg-black border-t border-white/10" data-testid="footer">
      <div className="h-px w-full gradient-line mb-16" />
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-16">
          <div className="md:col-span-2">
            <h3 className="font-unbounded text-2xl font-bold text-primary mb-4">UPCLOSE DIGITAL</h3>
            <p className="text-muted-foreground max-w-md">Leading manufacturer and supplier of premium LED display solutions. Illuminating visions across Malaysia.</p>
          </div>
          <div>
            <h4 className="font-unbounded font-semibold mb-4">Quick Links</h4>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.name}><a href={link.href} className="text-muted-foreground hover:text-primary transition-colors">{link.name}</a></li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className="font-unbounded font-semibold mb-4">Company</h4>
            <ul className="space-y-2">
              {otherLinks.map((link) => (
                <li key={link.name}><a href={link.href} className="text-muted-foreground hover:text-primary transition-colors">{link.name}</a></li>
              ))}
            </ul>
          </div>
        </div>
        <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-muted-foreground text-sm">© 2024 UpClose Digital. All rights reserved.</p>
          <div className="flex items-center gap-6">
            <a href="/admin/login" className="text-muted-foreground text-sm hover:text-primary transition-colors flex items-center gap-1" data-testid="admin-login-link">
              <Settings size={14} /> Admin Login
            </a>
            <p className="text-muted-foreground text-sm font-mono">upclose-digital.com</p>
          </div>
        </div>
      </div>
    </footer>
  );
};

const WhatsAppWidget = () => {
  return (
    <div className="fixed bottom-6 right-6 z-50" data-testid="whatsapp-widget">
      <div className="relative group">
        <span className="absolute -top-12 right-0 bg-black/90 text-foreground text-sm px-4 py-2 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap border border-white/10">
          Need help? Chat with us!
          <span className="absolute -bottom-1 right-4 w-2 h-2 bg-black/90 rotate-45 border-r border-b border-white/10" />
        </span>
        <a href="https://wa.me/60197866222" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center w-14 h-14 bg-accent rounded-full animate-glow-pulse hover:scale-110 transition-transform" data-testid="whatsapp-btn">
          <MessageCircle className="w-7 h-7 text-black" />
        </a>
      </div>
    </div>
  );
};

const HomePage = () => (
  <div className="min-h-screen bg-background text-foreground">
    <Navigation />
    <HeroSection />
    <ServicesSection />
    <ProductsSection />
    <GallerySection />
    <PermanentInstallationSection />
    <AudioSystemSection />
    <EventManagementSection />
    <AboutSection />
    <BlogSection />
    <FAQSection />
    <ContactSection />
    <Footer />
    <WhatsAppWidget />
  </div>
);

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Public Website */}
          <Route path="/" element={<HomePage />} />
          
          {/* Admin Routes */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={
            <ProtectedRoute>
              <AdminLayout><AdminDashboard /></AdminLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/products" element={
            <ProtectedRoute>
              <AdminLayout><AdminProducts /></AdminLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/blogs" element={
            <ProtectedRoute>
              <AdminLayout><AdminBlogs /></AdminLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/gallery" element={
            <ProtectedRoute>
              <AdminLayout><AdminGallery /></AdminLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/faqs" element={
            <ProtectedRoute>
              <AdminLayout><AdminFAQs /></AdminLayout>
            </ProtectedRoute>
          } />
          <Route path="/admin/messages" element={
            <ProtectedRoute>
              <AdminLayout><AdminMessages /></AdminLayout>
            </ProtectedRoute>
          } />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
